import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Empty, Select as AntSelect } from 'antd';
import { useTranslation } from 'react-i18next';
import { DefaultOptionType as AntdOptionProps } from 'antd/lib/select';
import { Flex } from '..';
import {
  NumberOfSelectedFlex,
  OptionAll,
  SelectArrowFlex,
  SelectError,
  SelectTickFlex,
  SelectWrapper,
  StyledDivider,
  StyledSelect,
  IconsWrapper,
} from './styled';
import {
  NavigationAndActionArrowDown,
  NavigationAndActionArrowUp,
  NavigationAndActionSingleTick,
} from '../icons';
import { useSelectValidator } from '../../hooks/validationHooks/useSelectValidator';
import { ValidationType } from '../../../types/ValidationType';
import { IconContainer, StyledClearIcon } from '../input/input/styled';

export interface SelectOptionProps extends AntdOptionProps {}

export interface CustomSelectProps {
  options: SelectOptionProps[];
  readOnly?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  placeholderText?: string;
  errorText?: string;
  timeoutOnInputChangeInMs?: number;
  required?: boolean;
  defaultValue?: string | number;
  searchable?: boolean;
  onChange?: (arg: any, option?: SelectOptionProps) => void;
  onSelectedValue?: (arg: any) => void;
  validators?: ValidationType[];
  validate?: boolean;
  name?: string;
}

const { Option } = AntSelect;

export const Select: React.FC<CustomSelectProps> = ({
  timeoutOnInputChangeInMs = 100,
  options,
  readOnly,
  disabled,
  multiple,
  placeholderText,
  errorText,
  defaultValue,
  searchable = true,
  onChange,
  onSelectedValue,
  validate,
  validators,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedValues, setSelectedValues] = useState<SelectOptionProps[]>([]);
  const [lastErrorText, setLastErrorText] = useState<string | undefined>(
    errorText,
  );

  const validateField = useSelectValidator();
  const [showError, setShowError] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>();

  const [showClearIcon, setShowClearIcon] = useState(false);
  const [clearButtonClicked, setClearButtonClicked] = useState(false);

  const onChangeSelect = useCallback(
    (values, option) => {
      option ? setSelectedValues([option]) : setSelectedValues([]);
      onSelectedValue?.(values);
      if (
        Array.isArray(values) &&
        JSON.stringify([...values].sort()) ===
          JSON.stringify([...options].sort())
      ) {
        setIsAllSelected(true);
      } else {
        setIsAllSelected(false);
      }

      if (values && values.length !== 0) {
        setShowClearIcon(true);
      } else setShowClearIcon(false);

      onChange && onChange(values, option);
    },
    [onSelectedValue, options, onChange],
  );

  useEffect(() => {
    if (selectedValues.length === 0 && defaultValue && !clearButtonClicked) {
      const defaultOption = options?.find((o) => o?.value === defaultValue);
      if (defaultOption) {
        onChangeSelect(defaultValue, defaultOption);
      }
    }

    if (!defaultValue) {
      setSelectedValues([]);
    }
  }, [
    clearButtonClicked,
    defaultValue,
    onChangeSelect,
    options,
    selectedValues.length,
  ]);

  useEffect(() => {
    const valError = validators
      ? validateField(selectedValues, validators)
      : undefined;
    setValidationError(valError);

    let error = errorText;
    if (validate && valError) {
      error = valError;
    }
    setLastErrorText(error);
    setShowError(error !== undefined && error.length > 0);
  }, [errorText, selectedValues, validate, validators, validateField]);

  useEffect(() => {
    if (validators) {
      setValidationError(validateField(selectedValues, validators));
    }
  }, [validators, selectedValues, validateField]);

  useEffect(() => {
    setShowClearIcon(!!defaultValue);
  }, [defaultValue]);

  const checkAll = useCallback(() => {
    if (
      JSON.stringify([...selectedValues].sort()) ===
      JSON.stringify([...options].sort())
    ) {
      setSelectedValues([]);
      setIsAllSelected(false);
    } else {
      setSelectedValues([...options]);
      setIsAllSelected(true);
    }
  }, [selectedValues, options]);

  const handleClearIconClick = () => {
    setShowClearIcon(false);
    setClearButtonClicked(true);
    onChangeSelect('', undefined);
  };

  const isEditable = useMemo(() => {
    if (!(disabled || readOnly)) {
      return true;
    }
    return false;
  }, [disabled, readOnly]);

  return (
    <SelectWrapper>
      <Flex justifyContent="center" width="100%">
        <StyledSelect
          $hasError={showError && isEditable}
          onChange={onChangeSelect}
          readOnly={readOnly}
          multiple={multiple}
          mode={multiple ? 'multiple' : undefined}
          disabled={disabled}
          allowClear={false}
          showSearch={searchable}
          optionFilterProp={searchable ? 'label' : undefined}
          filterOption={
            searchable
              ? (input, option) =>
                  (option?.label as string)
                    .toLowerCase()
                    .startsWith(input.toLowerCase())
              : undefined
          }
          notFoundContent={
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              imageStyle={{
                height: 30,
              }}
              description={t('select.noData')}
            />
          }
          showArrow={false}
          defaultActiveFirstOption={false}
          onDropdownVisibleChange={() => setIsOpen(!isOpen)}
          placeholder={placeholderText}
          value={selectedValues}
          className={
            validationError && validators ? 'form-input-not-valid' : ''
          }
          getPopupContainer={(triggerNode) => triggerNode as HTMLElement}
          dropdownRender={(menu) => (
            <>
              <OptionAll
                multiple={multiple}
                onClick={checkAll}
                selected={isAllSelected}>
                {t('select.selectAll')}
                <SelectTickFlex multiple={multiple} alignItems="center">
                  <NavigationAndActionSingleTick />
                </SelectTickFlex>
              </OptionAll>
              <StyledDivider multiple={multiple} />
              {menu}
            </>
          )}>
          {options?.map((option) => (
            <Option
              key={option?.value}
              label={option?.label}
              value={option?.value}
              name={option?.name}>
              {option?.label}
              <SelectTickFlex multiple={multiple} alignItems="center">
                <NavigationAndActionSingleTick />
              </SelectTickFlex>
            </Option>
          ))}
        </StyledSelect>
      </Flex>
      {multiple && selectedValues.length !== 0 && (
        <NumberOfSelectedFlex>({selectedValues.length})</NumberOfSelectedFlex>
      )}
      <IconsWrapper>
        {showClearIcon && isEditable && (
          <IconContainer onClick={handleClearIconClick}>
            <StyledClearIcon />
          </IconContainer>
        )}
      </IconsWrapper>
      <SelectArrowFlex disabled={disabled} readOnly={readOnly}>
        {isOpen ? (
          <NavigationAndActionArrowUp />
        ) : (
          <NavigationAndActionArrowDown />
        )}
      </SelectArrowFlex>
      <SelectError hasError={showError && isEditable}>
        <p>{lastErrorText}</p>
      </SelectError>
    </SelectWrapper>
  );
};
